import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  FiPlus, FiEdit2, FiTrash2, FiSearch, FiX,
  FiUser, FiHome, FiPhone, FiMail, FiTruck,
  FiChevronDown, FiChevronUp, FiAlertCircle, FiCheck
} from 'react-icons/fi';
import './Residents.css';
import PlacaVeicular from '../components/PlacaVeicular';   // Onda 190
import EvidenciaDoAcesso from '../components/EvidenciaDoAcesso';   // Onda 191
import '../styles/BuscaPorPlaca.css';                      // Onda 190

const STATUS_LABELS = { ativo: 'Ativo', inativo: 'Inativo' };
const STATUS_COLORS = { ativo: 'success', inativo: 'danger' };

export default function Residents() {
  const [residents, setResidents]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterBlock, setFilterBlock]   = useState('');
  const [blocks, setBlocks]         = useState([]);
  const [expanded, setExpanded]     = useState(null);
  const [vehicles, setVehicles]     = useState({});
  const [modal, setModal]           = useState(null); // { type: 'resident'|'vehicle', data, residentId }
  const [form, setForm]             = useState({});
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');

  const fetchResidents = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search)       params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (filterBlock)  params.block  = filterBlock;
      const res = await api.get('/api/residents', { params });
      setResidents(res.data.data || []);
    } catch (e) {
      setError('Erro ao carregar moradores');
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, filterBlock]);

  useEffect(() => { fetchResidents(); }, [fetchResidents]);

  useEffect(() => {
    api.get('/api/residents/meta/blocks')
      .then(r => setBlocks(r.data.data || []))
      .catch(() => {});
  }, []);

  const toggleExpand = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!vehicles[id]) {
      try {
        const res = await api.get(`/api/residents/${id}/vehicles`);
        setVehicles(v => ({ ...v, [id]: res.data.data || [] }));
      } catch (e) {}
    }
  };

  const openModal = (type, data = {}, residentId = null) => {
    setForm(data);
    setModal({ type, data, residentId });
    setError('');
  };

  const closeModal = () => { setModal(null); setForm({}); setError(''); };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (modal.type === 'resident') {
        if (modal.data?.id) {
          await api.put(`/api/residents/${modal.data.id}`, form);
          setSuccess('Morador atualizado com sucesso');
        } else {
          await api.post('/api/residents', form);
          setSuccess('Morador cadastrado com sucesso');
        }
        fetchResidents();
      } else if (modal.type === 'vehicle') {
        if (modal.data?.id) {
          await api.put(`/api/residents/vehicles/${modal.data.id}`, form);
          setSuccess('Veículo atualizado com sucesso');
        } else {
          await api.post(`/api/residents/${modal.residentId}/vehicles`, form);
          setSuccess('Veículo cadastrado com sucesso');
        }
        // Recarregar veículos do morador
        const res = await api.get(`/api/residents/${modal.residentId}/vehicles`);
        setVehicles(v => ({ ...v, [modal.residentId]: res.data.data || [] }));
      }
      closeModal();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type, id, residentId = null) => {
    if (!window.confirm(type === 'resident'
      ? 'Desativar este morador?'
      : 'Remover este veículo?')) return;
    try {
      if (type === 'resident') {
        await api.delete(`/api/residents/${id}`);
        fetchResidents();
        setSuccess('Morador desativado');
      } else {
        await api.delete(`/api/residents/vehicles/${id}`);
        const res = await api.get(`/api/residents/${residentId}/vehicles`);
        setVehicles(v => ({ ...v, [residentId]: res.data.data || [] }));
        setSuccess('Veículo removido');
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.message || 'Erro ao excluir');
    }
  };

  return (
    <div className="residents-page">
      <div className="page-header">
        <div>
          <h1>🏠 Moradores</h1>
          <p>Gestão de moradores e veículos cadastrados — Vertical Condomínio</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal('resident')}>
          <FiPlus /> Novo Morador
        </button>
      </div>

      {success && <div className="alert alert-success"><FiCheck /> {success}</div>}
      {error && !modal && <div className="alert alert-danger"><FiAlertCircle /> {error}</div>}

      {/* Filtros */}
      <div className="filters-bar">
        <div className="search-box">
          <FiSearch />
          <input
            placeholder="Buscar por nome, unidade, bloco ou placa..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button onClick={() => setSearch('')}><FiX /></button>}
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Todos os status</option>
          <option value="ativo">Ativo</option>
          <option value="inativo">Inativo</option>
        </select>
        <select value={filterBlock} onChange={e => setFilterBlock(e.target.value)}>
          <option value="">Todos os blocos</option>
          {blocks.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>

      {/* Tabela */}
      {loading ? (
        <div className="loading-state">Carregando moradores...</div>
      ) : residents.length === 0 ? (
        <div className="empty-state">
          <FiUser size={48} />
          <h3>Nenhum morador cadastrado</h3>
          <p>Clique em "Novo Morador" para começar.</p>
        </div>
      ) : (
        <div className="residents-list">
          {residents.map(r => (
            <div key={r.id} className={`resident-card ${r.status === 'inativo' ? 'inactive' : ''}`}>
              <div className="resident-card-header" onClick={() => toggleExpand(r.id)}>
                <div className="resident-info">
                  <div className="resident-avatar">
                    {r.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3>
                      {r.name}
                      {/* Onda 110: de onde veio este cadastro, e se alguém o
                          alterou aqui. Quem vem da API é sobrescrito na próxima
                          carga do sistema de gestão. */}
                      {(r.external_code || r.last_seen_sync_id) && (
                        <span className="res-origem" title="Cadastro recebido pela API de Integração">via API</span>
                      )}
                      {r.edited_at && (
                        <span className="res-origem editado"
                          title={`Editado nesta tela em ${new Date(r.edited_at).toLocaleString('pt-BR')}`}>
                          editado aqui
                        </span>
                      )}
                    </h3>
                    <div className="resident-meta">
                      <span><FiHome size={12} /> {r.block ? `${r.block} — ` : ''}{r.unit}</span>
                      {r.phone && <span><FiPhone size={12} /> {r.phone}</span>}
                      {r.email && <span><FiMail size={12} /> {r.email}</span>}
                    </div>
                    {/* Onda 190: os veículos cuja placa bateu com a busca, logo abaixo do morador. */}
                    {r.matched_vehicles && r.matched_vehicles.length > 0 && (
                      <ul className="busca-placas">
                        {r.matched_vehicles.map(v => (
                          <li key={v.id} title="Ver os veículos do morador">
                            <PlacaVeicular placa={v.plate} tamanho="p" />
                            <span className="busca-placas-modelo">{[v.brand, v.model].filter(Boolean).join(' ') || 'marca e modelo não informados'}</span>
                            <span className={`busca-placas-situacao ${v.status === 'inativo' ? 'inativo' : 'ativo'}`}>{v.status === 'inativo' ? 'veículo inativo' : 'veículo ativo'}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="resident-actions">
                  <span className={`badge badge-${STATUS_COLORS[r.status]}`}>
                    {STATUS_LABELS[r.status]}
                  </span>
                  <span className="vehicle-count">
                    <FiTruck size={14} /> {r.vehicle_count} veículo{r.vehicle_count !== 1 ? 's' : ''}
                  </span>
                  <button className="btn btn-icon" onClick={e => { e.stopPropagation(); openModal('resident', r); }}>
                    <FiEdit2 />
                  </button>
                  <button className="btn btn-icon btn-danger" onClick={e => { e.stopPropagation(); handleDelete('resident', r.id); }}>
                    <FiTrash2 />
                  </button>
                  {expanded === r.id ? <FiChevronUp /> : <FiChevronDown />}
                </div>
              </div>

              {/* Veículos expandidos */}
              {expanded === r.id && (
                <div className="vehicles-panel">
                  <div className="vehicles-panel-header">
                    <h4>Veículos Cadastrados</h4>
                    <button className="btn btn-sm btn-primary" onClick={() => openModal('vehicle', {}, r.id)}>
                      <FiPlus /> Adicionar Veículo
                    </button>
                  </div>
                  {!vehicles[r.id] ? (
                    <p className="loading-text">Carregando...</p>
                  ) : vehicles[r.id].length === 0 ? (
                    <p className="empty-text">Nenhum veículo cadastrado para este morador.</p>
                  ) : (
                    <div className="vehicles-grid">
                      {vehicles[r.id].map(v => (
                        <div key={v.id} className={`vehicle-card ${v.status === 'inativo' ? 'inactive' : ''}`}>
                          <div className="plate-badge">{v.plate}</div>
                          <div className="vehicle-details">
                            {v.brand && <span>{v.brand}</span>}
                            {v.model && <span>{v.model}</span>}
                            {v.color && <span>{v.color}</span>}
                            {v.year  && <span>{v.year}</span>}
                          </div>
                          <div className="vehicle-ultimo-acesso">
                            <span className="vehicle-ultimo-acesso-rotulo">Último acesso</span>
                            <EvidenciaDoAcesso acesso={v.last_access} placa={v.plate} />
                          </div>
                          <div className="vehicle-actions">
                            <span className={`badge badge-${v.status === 'ativo' ? 'success' : 'danger'}`}>
                              {v.status === 'ativo' ? 'Ativo' : 'Bloqueado'}
                            </span>
                            <button className="btn btn-icon" onClick={() => openModal('vehicle', v, r.id)}>
                              <FiEdit2 />
                            </button>
                            <button className="btn btn-icon btn-danger" onClick={() => handleDelete('vehicle', v.id, r.id)}>
                              <FiTrash2 />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {modal.type === 'resident'
                  ? (modal.data?.id ? 'Editar Morador' : 'Novo Morador')
                  : (modal.data?.id ? 'Editar Veículo' : 'Adicionar Veículo')}
              </h2>
              <button onClick={closeModal}><FiX /></button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger"><FiAlertCircle /> {error}</div>}

              {modal.type === 'resident' ? (
                <div className="form-grid">
                  <div className="form-group full">
                    <label>Nome Completo *</label>
                    <input value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} placeholder="Nome do morador" />
                  </div>
                  <div className="form-group">
                    <label>Unidade *</label>
                    <input value={form.unit || ''} onChange={e => setForm({...form, unit: e.target.value})} placeholder="Ex: 101, 202A" />
                  </div>
                  <div className="form-group">
                    <label>Bloco / Torre</label>
                    <input value={form.block || ''} onChange={e => setForm({...form, block: e.target.value})} placeholder="Ex: A, B, Torre 1" />
                  </div>
                  <div className="form-group">
                    <label>CPF / CNPJ</label>
                    <input value={form.document || ''} onChange={e => setForm({...form, document: e.target.value})} placeholder="000.000.000-00" />
                  </div>
                  <div className="form-group">
                    <label>Telefone</label>
                    <input value={form.phone || ''} onChange={e => setForm({...form, phone: e.target.value})} placeholder="(00) 00000-0000" />
                  </div>
                  <div className="form-group full">
                    <label>E-mail</label>
                    <input type="email" value={form.email || ''} onChange={e => setForm({...form, email: e.target.value})} placeholder="email@exemplo.com" />
                  </div>
                  {modal.data?.id && (
                    <div className="form-group">
                      <label>Status</label>
                      <select value={form.status || 'ativo'} onChange={e => setForm({...form, status: e.target.value})}>
                        <option value="ativo">Ativo</option>
                        <option value="inativo">Inativo</option>
                      </select>
                    </div>
                  )}
                  <div className="form-group full">
                    <label>Observações</label>
                    <textarea value={form.notes || ''} onChange={e => setForm({...form, notes: e.target.value})} rows={3} placeholder="Informações adicionais..." />
                  </div>
                </div>
              ) : (
                <div className="form-grid">
                  <div className="form-group full">
                    <label>Placa *</label>
                    <input
                      value={form.plate || ''}
                      onChange={e => setForm({...form, plate: e.target.value.toUpperCase()})}
                      placeholder="ABC1D23 ou ABC1234"
                      maxLength={8}
                      style={{ fontFamily: 'monospace', fontSize: '1.2rem', letterSpacing: '0.1em' }}
                      disabled={!!modal.data?.id}
                    />
                    {!modal.data?.id && <small>Formato Mercosul (ABC1D23) ou antigo (ABC1234)</small>}
                  </div>
                  <div className="form-group">
                    <label>Marca</label>
                    <input value={form.brand || ''} onChange={e => setForm({...form, brand: e.target.value})} placeholder="Ex: Toyota" />
                  </div>
                  <div className="form-group">
                    <label>Modelo</label>
                    <input value={form.model || ''} onChange={e => setForm({...form, model: e.target.value})} placeholder="Ex: Corolla" />
                  </div>
                  <div className="form-group">
                    <label>Cor</label>
                    <input value={form.color || ''} onChange={e => setForm({...form, color: e.target.value})} placeholder="Ex: Prata" />
                  </div>
                  <div className="form-group">
                    <label>Ano</label>
                    <input type="number" value={form.year || ''} onChange={e => setForm({...form, year: e.target.value})} placeholder="2024" min="1990" max="2030" />
                  </div>
                  {modal.data?.id && (
                    <div className="form-group">
                      <label>Status</label>
                      <select value={form.status || 'ativo'} onChange={e => setForm({...form, status: e.target.value})}>
                        <option value="ativo">Ativo</option>
                        <option value="inativo">Bloqueado</option>
                      </select>
                    </div>
                  )}
                  <div className="form-group full">
                    <label>Observações</label>
                    <textarea value={form.notes || ''} onChange={e => setForm({...form, notes: e.target.value})} rows={2} placeholder="Informações adicionais..." />
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
